import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { Guide } from './types';
import { guideHasConclusion } from './types';
import { cn } from '@ifixit/ui/lib/utils';
import { isModalOpen } from 'Shared/editor/shortcut_scope';
import { GuideGate } from './GuideGate';
import { EmbedHeader } from './EmbedHeader';
import { useFullscreen } from './useFullscreen';
import { useParentBridge } from './useParentBridge';
import { Rail } from './Rail';
import { StepNav } from './StepNav';
import { TitleScreen } from './TitleScreen';
import { IntroScreen } from './IntroScreen';
import { StepScreen } from './StepScreen';
import { VideoStepScreen } from './VideoStepScreen';
import { ConclusionScreen } from './ConclusionScreen';
import { headerLabel, progressPercent, screenLabel } from './labels';

// index space: 0 = intro, 1..N = steps, N+1 = conclusion.
const INTRO_INDEX = 0;

function parseStepHash(totalSteps: number): number | null {
   const match = window.location.hash.match(/^#s(\d+)$/);
   if (!match) {
      return null;
   }
   const requested = Number.parseInt(match[1], 10);
   return requested >= 1 && requested <= totalSteps ? requested : null;
}

export function EmbedApp() {
   return (
      <GuideGate wrapper={StatusOverlay}>{guide => <EmbedGuideView guide={guide} />}</GuideGate>
   );
}

function EmbedGuideView({ guide }: { guide: Guide }) {
   const totalSteps = guide.steps.length;
   const isVideoOnly = Boolean(guide.isVideoOnly);
   const hasSteps = totalSteps > 0 && !isVideoOnly;
   // A conclusion slot exists only when the guide actually rendered one: not on
   // teardowns, and not when conclusion_rendered is empty. Otherwise nav caps at
   // the last step (mirrors the vanilla disabling Next there, and the bridge's
   // own maxIndex) instead of paging into an empty Conclusion screen.
   const hasConclusion = guideHasConclusion(guide);
   const lastIndex = totalSteps + (hasConclusion ? 1 : 0);
   const [requestedStep] = useState(() => parseStepHash(totalSteps));

   const [started, setStarted] = useState(requestedStep !== null);
   const [index, setIndex] = useState(requestedStep ?? INTRO_INDEX);
   // Mobile drawer open; desktop docked column collapsed. Separate because the
   // header toggle means different things on each side of the breakpoint.
   const [railOpen, setRailOpen] = useState(false);
   const [railCollapsed, setRailCollapsed] = useState(false);
   const fullscreen = useFullscreen();
   const contentRef = useRef<HTMLDivElement>(null);
   // Counts navigations, not index changes: a chaptered video guide moves the
   // index from playback every time the playhead crosses a chapter, and a reader
   // who scrolled down to the step text should not be pulled back up for that.
   const [navCount, setNavCount] = useState(0);

   const goTo = useCallback(
      (next: number) => {
         const clamped = Math.max(INTRO_INDEX, Math.min(lastIndex, next));
         // A navigation that lands where the reader already is (an arrow key at
         // either end, the drawer's Back row) must not count, or the effect
         // below pulls them back to the top of the screen they were reading.
         if (clamped === index) {
            return;
         }
         setIndex(clamped);
         setNavCount(count => count + 1);
      },
      [lastIndex, index]
   );

   // Below the breakpoint the toggle slides the drawer; at or above it docks or
   // hides the column (matching RailController's js-rail-toggle handler).
   const toggleRail = useCallback(() => {
      if (window.matchMedia('(max-width: 767px)').matches) {
         setRailOpen(open => !open);
      } else {
         setRailOpen(false);
         setRailCollapsed(collapsed => !collapsed);
      }
   }, []);

   // Jumping from the rail navigates and closes the mobile drawer. The drawer
   // only slides off screen, so the row that was tapped would otherwise stay
   // focused while hidden; hand focus to the content instead.
   const jumpToStep = useCallback(
      (step: number) => {
         goTo(step);
         if (railOpen) {
            setRailOpen(false);
            contentRef.current?.focus();
         }
      },
      [goTo, railOpen]
   );

   const start = useCallback(() => {
      const requested = parseStepHash(totalSteps);
      if (requested !== null) {
         setIndex(requested);
      }
      setStarted(true);
   }, [totalSteps]);

   // The video poller advances the visible step as playback crosses each
   // chapter. Identity-stable so it never rebuilds the sync hook's listeners.
   const onVideoStep = useCallback((stepNumber: number) => setIndex(stepNumber), []);

   // A host command navigates into the guide, leaving the title screen if needed.
   const handleGoToStep = useCallback(
      (next: number) => {
         setStarted(true);
         goTo(next);
      },
      [goTo]
   );

   // Bridge to the FixBot host frame (inert without a trusted parent origin). The
   // title screen reports stepNumber -1 (no step) until the guide is started.
   const bridge = useParentBridge({
      guide,
      stepNumber: started ? index : -1,
      onGoToStep: handleGoToStep,
   });

   const canPrev = index > INTRO_INDEX;
   const canNext = index < lastIndex;

   // Left/right arrow keys page through the guide, matching the vanilla hint.
   useEffect(() => {
      if (!started || !hasSteps) {
         return;
      }
      function onKey(e: KeyboardEvent) {
         if (e.target instanceof HTMLElement && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) {
            return;
         }
         // Don't page the guide underneath an open modal. The zoom lightbox is a
         // Radix dialog, which sets no aria-modal, so match it by its open state.
         if (isModalOpen()) {
            return;
         }
         if (e.key === 'ArrowRight') {
            goTo(index + 1);
         } else if (e.key === 'ArrowLeft') {
            goTo(index - 1);
         }
      }
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [started, hasSteps, index, goTo]);

   // The content column scrolls under the fixed header; a new screen has to
   // start at its own top rather than inheriting the last one's scroll offset.
   useEffect(() => {
      const content = contentRef.current;
      if (navCount === 0 || !content) {
         return;
      }
      content.scrollTop = 0;
      // The pager lives inside the keyed screen, so using it destroys the
      // button that was clicked and focus falls to <body>, which restarts a
      // keyboard reader at the top of the document. Catch it here, and only
      // here: the header arrows and the rail rows survive their own click, and
      // a reader paging with them keeps the focus they put there.
      if (document.activeElement === document.body || document.activeElement === null) {
         content.focus();
      }
   }, [navCount]);

   if (!started) {
      return <TitleScreen guide={guide} onStart={start} />;
   }

   // The rail docks as a left column ≥ breakpoint unless collapsed; shift the
   // fixed header and the content over to clear it.
   const railDocked = hasSteps && !railCollapsed;

   const title = headerLabel(guide, index);

   const nav = hasSteps ? (
      <StepNav
         prevLabel={canPrev ? screenLabel(guide, index - 1) : null}
         nextLabel={canNext ? screenLabel(guide, index + 1) : null}
         onPrev={() => goTo(index - 1)}
         onNext={() => goTo(index + 1)}
      />
   ) : null;

   return (
      <>
         {hasSteps && (
            <Rail
               guide={guide}
               index={index}
               totalSteps={totalSteps}
               open={railOpen}
               collapsed={railCollapsed}
               onJump={jumpToStep}
            />
         )}
         <EmbedHeader
            title={title}
            onRailToggle={toggleRail}
            onPrev={() => goTo(index - 1)}
            onNext={() => goTo(index + 1)}
            canPrev={canPrev}
            canNext={canNext}
            showNav={hasSteps}
            showRailToggle={hasSteps}
            railDocked={railDocked}
            percent={progressPercent(index, totalSteps)}
            onFullscreen={fullscreen.toggle}
            fullscreenSupported={fullscreen.supported}
            isFullscreen={fullscreen.isFullscreen}
         />
         {/* The embed shell (embed_css.phtml) is a fixed viewport: body sets
             overflow:hidden and expects the app to own its scroll container, the
             way vanilla's `section.page` did. Without this, anything taller than
             the viewport is simply unreachable. Screens clear the fixed header
             with their own top padding, so the whole column scrolls under it. */}
         <div
            ref={contentRef}
            // Named and focusable so the focus each navigation moves here
            // announces the screen that arrived. Not in the tab order, so the
            // ring it would draw around the whole column is no loss.
            role="region"
            aria-label={title}
            tabIndex={-1}
            className={cn(
               'embed-scroll fixed inset-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]',
               'outline-none transition-[padding] duration-200 motion-reduce:transition-none',
               railDocked && 'md:pl-[260px]'
            )}
         >
            {/* Keyed so the arriving screen replays its entrance. A video guide
                keeps one key: its player is mounted once on purpose, and
                remounting it reloads the video (see useGuideVideoSync). */}
            <div key={guide.isVideoGuide ? 'video' : index} className="embed-screen">
               {renderScreen(guide, index, onVideoStep, bridge.notifyImageChange, nav)}
            </div>
         </div>
      </>
   );
}

function renderScreen(
   guide: Guide,
   index: number,
   onVideoStep: (n: number) => void,
   onImageChange: (stepNumber: number, imageIndex: number) => void,
   footer: ReactNode
) {
   if (index === INTRO_INDEX) {
      return <IntroScreen guide={guide} footer={footer} />;
   }
   if (index > guide.steps.length) {
      return <ConclusionScreen guide={guide} footer={footer} />;
   }
   // A chaptered video guide keeps one persistent player across its steps, so
   // every step renders through the same VideoStepScreen position in the tree.
   if (guide.isVideoGuide) {
      return (
         <VideoStepScreen
            guide={guide}
            stepIndex={index}
            onVideoStep={onVideoStep}
            footer={footer}
         />
      );
   }
   // Key per step so the image gallery's active-image/zoom state resets on nav
   // (unlike the video screen, which is deliberately persistent).
   const step = guide.steps[index - 1];
   return (
      <StepScreen
         key={step.stepid}
         step={step}
         footer={footer}
         onImageChange={imageIndex => onImageChange(index, imageIndex)}
      />
   );
}

function StatusOverlay({ children, role }: { children: ReactNode; role: 'status' | 'alert' }) {
   return (
      <div
         className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-white p-6 text-center text-gray-700"
         role={role}
         aria-live={role === 'status' ? 'polite' : undefined}
      >
         {children}
      </div>
   );
}
